import React from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

// ─── Passive touch listener patch ────────────────────────────────────────────
// react-slick adds non-passive touchstart/touchmove listeners which block scroll.
// Patching here (after imports, before render) makes all such listeners passive,
// eliminating the "Added non-passive event listener" DevTools violation.
(function patchPassiveListeners() {
  if (typeof window === 'undefined') return;
  const orig = EventTarget.prototype.addEventListener;
  EventTarget.prototype.addEventListener = function (type, fn, opts) {
    if (type === 'touchstart' || type === 'touchmove' || type === 'wheel') {
      // Respect explicit passive: false so wheel handlers can call preventDefault()
      if (typeof opts === 'object' && opts.passive === false) {
        return orig.call(this, type, fn, opts);
      }
      const normalized = typeof opts === 'object'
        ? { ...opts, passive: true }
        : { passive: true, capture: !!opts };
      return orig.call(this, type, fn, normalized);
    }
    return orig.call(this, type, fn, opts);
  };
})();
// ─────────────────────────────────────────────────────────────────────────────

// Deterministic cache key ensures Emotion generates the same class names
// during react-snap pre-render AND on client hydration → no class-name mismatch → no FOUC.
// insertionPoint pins Emotion's prepend:true behavior to right after the
// emotion-insertion-point marker in public/index.html — without it, Emotion
// prepends to the very top of <head>, ahead of <meta charset>, which pushes
// that tag past the first 1024 bytes (an HTML5 conformance error) in both
// react-snap's static output and a real page load.
const emotionCache = createCache({
  key: 'css',
  prepend: true,
  insertionPoint: document.querySelector('meta[name="emotion-insertion-point"]') || undefined,
});

const rootElement = document.getElementById('root');

// Nothing in this app legitimately embeds the whole site in an iframe of
// itself. The only iframe usage anywhere is SpotlightMicrositePage embedding
// an external static microsite file — but a broken relative link (or, worse,
// a microsite's own broken AJAX call — e.g. one that fetches a relative path
// that doesn't exist here and gets this app's index.html back instead, then
// injects it via jQuery's .html(), which DOES execute embedded <script>
// tags) can load this ENTIRE app's bundle a second time inside that iframe,
// producing a nested site-in-a-box (its own Header, its own router). Only
// remove the specific injected #root element this bundle would have tried
// to mount into — NOT the whole document body. In the AJAX-injection case,
// #root arrives as a fragment nested inside the microsite's own real,
// legitimate content (e.g. inside a broken footer widget); clearing the
// whole body there would wipe out that real content along with the junk.
if (window.self !== window.top) {
  if (rootElement) rootElement.remove();
} else {
  const AppTree = (
    <React.StrictMode>
      <CacheProvider value={emotionCache}>
        <HelmetProvider>
          <App />
        </HelmetProvider>
      </CacheProvider>
    </React.StrictMode>
  );

  // Always use createRoot — react-snap HTML is still served to SEO bots before JS loads.
  // hydrateRoot caused errors #418/#425 because snap captures post-data HTML while the
  // client starts with empty Redux state, making the initial render different every time.
  // createRoot never clears pre-existing markup in its container (only hydrateRoot does) —
  // on a mismatched deep-link fallback, #root still holds another page's baked-in snap HTML
  // (e.g. its Header), so without this it renders alongside it instead of replacing it,
  // producing a duplicate Header once index.html's snap-fallback-style is removed.
  rootElement.innerHTML = '';
  createRoot(rootElement).render(AppTree);
}

reportWebVitals();
